import { useEffect, useState } from "react";
import useAuthedApi from "../hooks/useAuthedApi";
import Badge from "../design-system/badge/Badge.jsx";
import CircleCheck from "../design-system/icons/CircleCheck.jsx";
import { HiExclamationTriangle } from "react-icons/hi2";

// ── Mini gauge ─────────────────────────────────────────────────────────────

function arcPath(cx, cy, r, startDeg, endDeg) {
  const toXY = (deg) => ({
    x: cx + r * Math.cos((deg * Math.PI) / 180),
    y: cy - r * Math.sin((deg * Math.PI) / 180),
  });
  const s = toXY(startDeg);
  const e = toXY(endDeg);
  const largeArc = Math.abs(endDeg - startDeg) > 180 ? 1 : 0;
  return `M ${s.x} ${s.y} A ${r} ${r} 0 ${largeArc} 0 ${e.x} ${e.y}`;
}

function MiniGauge({ mmrPct }) {
  const CAP = 600;
  const angle = mmrPct != null ? Math.min((mmrPct / CAP) * 180, 180) : 90;
  const cx = 40, cy = 37, r = 28;
  const toXY = (deg) => ({
    x: cx + r * Math.cos((deg * Math.PI) / 180),
    y: cy - r * Math.sin((deg * Math.PI) / 180),
  });
  const needle = toXY(angle);
  return (
    <svg viewBox="0 0 80 42" className="w-20 h-10 shrink-0">
      <path d={arcPath(cx, cy, r, 0, 60)}    fill="none" stroke="#FCA5A5" strokeWidth="9" strokeLinecap="round" />
      <path d={arcPath(cx, cy, r, 58, 122)}  fill="none" stroke="#FDE68A" strokeWidth="9" strokeLinecap="round" />
      <path d={arcPath(cx, cy, r, 120, 180)} fill="none" stroke="#6EE7B7" strokeWidth="9" strokeLinecap="round" />
      <line x1={cx} y1={cy} x2={needle.x} y2={needle.y} stroke="#374151" strokeWidth="2" strokeLinecap="round" />
      <circle cx={cx} cy={cy} r="3" fill="#374151" />
    </svg>
  );
}

// ── Helpers ────────────────────────────────────────────────────────────────

function getDir(bot) {
  return (bot.direction || bot.position_side || "").toUpperCase();
}


function mmrForBot(bot, risk) {
  if (!risk) return null;
  const dir = getDir(bot);
  if (dir === "SHORT") return risk.short?.mgnPct ?? null;
  if (dir === "LS2" || dir === "LS") {
    const l = risk.long?.mgnPct;
    const s = risk.short?.mgnPct;
    if (l != null && s != null) return Math.min(l, s);
    return l ?? s ?? null;
  }
  return risk.long?.mgnPct ?? null;
}

function riskLabelForBot(bot, risk) {
  if (!risk) return null;
  const dir = getDir(bot);
  if (dir === "SHORT") return risk.short?.riskLabel ?? null;
  if (dir === "LS2" || dir === "LS") {
    const l = risk.long?.mgnPct ?? Infinity;
    const s = risk.short?.mgnPct ?? Infinity;
    return l <= s ? (risk.long?.riskLabel ?? null) : (risk.short?.riskLabel ?? null);
  }
  return risk.long?.riskLabel ?? null;
}

// Status colours come from the design-system tokens: avocado (success),
// gold (warning) and devil (danger) — see design-system/tokens.css.
const RISK_COLOR = { "Low Risk": "#0F6D32", "Moderate": "#684E00", "Medium Risk": "#684E00" };
const RISK_BG    = { "Low Risk": "#EDFDF3", "Moderate": "#FDDC69", "Medium Risk": "#FDDC69" };
const RISK_MSG   = {
  "Low Risk":    "Ok — high MMR",
  "Moderate":    "Watch out for MMR level.",
  "Medium Risk": "Watch out for MMR level.",
};

function fmtDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  const months = ["January","February","March","April","May","June","July","August","September","October","November","December"];
  const offsetH = -d.getTimezoneOffset() / 60;
  const utc = `UTC${offsetH >= 0 ? "+" : ""}${offsetH}`;
  return `${months[d.getMonth()]} ${d.getDate()}, ${d.getFullYear()} ${String(d.getHours()).padStart(2,"0")}:${String(d.getMinutes()).padStart(2,"0")}:${String(d.getSeconds()).padStart(2,"0")} (${utc})`;
}

function fmt(n, dp = 3) {
  const v = parseFloat(n);
  return isNaN(v) ? "0" : v.toLocaleString("en-US", { minimumFractionDigits: dp, maximumFractionDigits: dp });
}

// ── Margin health box ──────────────────────────────────────────────────────
// `side` is set only for dual-side (LS) bots, which show one box per leg.

function MarginHealthBox({ side, mmr, riskLabel }) {
  const rLabel = riskLabel || (mmr != null
    ? mmr >= 300 ? "Low Risk" : mmr >= 150 ? "Moderate" : "High Risk"
    : null);
  const rColor = RISK_COLOR[rLabel] || "#CB1515";
  const rBg    = RISK_BG[rLabel] || "#FCDFDF";
  const rMsg   = RISK_MSG[rLabel] || (mmr != null ? "Low Margin, top up your account" : "No position data");
  const StatusIcon = rLabel === "Low Risk" ? CircleCheck : HiExclamationTriangle;

  return (
    <div className="border border-gray-200 rounded-xl bg-[#F8F9FB] p-3">
      <div className="flex items-center gap-3">
        <MiniGauge mmrPct={mmr} />
        <div className="min-w-0 flex-1">
          <p className="text-[10px] text-[#697077]">
            {side ? `${side} - ` : ""}Margin Health
          </p>
          <p className="text-base font-bold text-[#21272A]">
            {mmr != null ? `${parseFloat(mmr).toFixed(0)}%` : "—"}
          </p>
        </div>
        {rLabel && (
          <span className="text-[11px] font-semibold shrink-0" style={{ color: rColor }}>{rLabel}</span>
        )}
      </div>
      <div
        className="flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 mt-2 text-[11px] font-medium"
        style={{ background: rBg, color: rColor }}
      >
        <StatusIcon className="w-3.5 h-3.5 shrink-0" />
        {rMsg}
      </div>
    </div>
  );
}

// ── Card ───────────────────────────────────────────────────────────────────

export default function BotCard({ bot, onView }) {
  const { authedGet, authedPost } = useAuthedApi();
  const [winRate, setWinRate] = useState(null);
  const [risk, setRisk] = useState(null);

  useEffect(() => {
    authedGet(`/api/borrow/${bot.instance_id}/status/`)
      .then((r) => setWinRate(r.data?.win_rate ?? null))
      .catch(() => {});
    const isDryRunBot = bot.config_snapshot?.dry_run === true;
    if (bot.status === "running" && !isDryRunBot) {
      authedPost("/api/borrow/position-risk/", { symbol: bot.symbol })
        .then((r) => setRisk(r.data))
        .catch(() => {});
    }
  }, [bot.instance_id, bot.symbol, bot.status]); // eslint-disable-line react-hooks/exhaustive-deps

  const pair = (bot.symbol || "").replace(/USDT$/, "/USDT");
  const initials = (bot.symbol || "?").replace("USDT", "").slice(0, 3).toUpperCase();
  const isRunning = bot.status === "running";
  const pnl = parseFloat(bot.realized_pnl || 0);
  const dir = getDir(bot);
  const isDualSide = dir === "LS2" || dir === "LS" || dir === "LS6";
  // initial_capital is stored AFTER leverage for LONG and LS/LS2/LS6 (confirmed:
  // collateral_per_trade x number_of_grids x leverage, doubled across legs for LS),
  // same convention as BotDetailPage.jsx -- normalize here too so the card's capital
  // and PNL% match what the detail page shows for the same bot.
  const rawCapital = parseFloat(bot.initial_capital || 0);
  const capital = ((dir === "LONG" || isDualSide) && bot.leverage) ? rawCapital / bot.leverage : rawCapital;
  const pnlPct = capital > 0 ? (pnl / capital) * 100 : 0;

  const isDryRun = bot.config_snapshot?.dry_run === true;
  const dirColor = "gray";
  const dirLabel = dir === "SHORT" ? "SHORT" : isDualSide ? "LONG+SHORT" : "LONG";
  const mmr = mmrForBot(bot, risk);
  const rLabel = riskLabelForBot(bot, risk);

  return (
    <div className="w-full desktop:max-w-[340px] bg-white rounded-xl border border-gray-200 p-4 flex flex-col gap-3 hover:shadow-sm transition-shadow">
      {/* Header */}
      <div className="flex items-start gap-2">
        <div className="relative shrink-0">
          <div className="w-8 h-8 rounded-full bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center text-[10px] font-bold text-white">
            {initials}
          </div>
          <img
            src="/icon_okx.svg"
            alt="OKX"
            className="absolute -bottom-0.5 -right-0.5 w-3.5 h-3.5 rounded-full border-2 border-white bg-white"
          />
        </div>
        <div className="min-w-0 flex-1">
          <span className="font-semibold text-[#21272A] text-sm block">{pair}</span>
          <span className="text-[10px] text-[#697077]">
            {bot.credential_label ? `${bot.credential_label} · Xenoarc` : "Xenoarc"}
          </span>
        </div>
        <div className="flex items-center gap-1.5 shrink-0 flex-wrap justify-end">
          <Badge color={dirColor} badgeStyle="stroke" label={dirLabel} size="small" />
          {isDryRun && <Badge color="orange" badgeStyle="lighter" label="SIM" size="small" />}
          {isRunning
            ? <Badge color="blue" badgeStyle="lighter" type="dot" label="ACTIVE" size="small" />
            : <Badge color="gray" badgeStyle="lighter" label={bot.status.toUpperCase()} size="small" />
          }
        </div>
      </div>

      {/* Margin Health — hidden for SIM bots and inactive bots */}
      {!isDryRun && isRunning && (isDualSide ? (
        <div className="space-y-2">
          <MarginHealthBox side="LONG" mmr={risk?.long?.mgnPct} riskLabel={risk?.long?.riskLabel} />
          <MarginHealthBox side="SHORT" mmr={risk?.short?.mgnPct} riskLabel={risk?.short?.riskLabel} />
        </div>
      ) : (
        <MarginHealthBox side={dir} mmr={mmr} riskLabel={rLabel} />
      ))}

      {/* Stats */}
      <div className="grid grid-cols-2 gap-3 pt-2.5 border-t border-gray-100">
        <div>
          <p className="text-[11px] text-[#697077]">Realized Profit</p>
          <p className="text-sm font-semibold text-[#21272A]">{fmt(pnl)} USDT</p>
          {pnl !== 0 ? (
            <p className={`text-[10px] font-medium ${pnlPct >= 0 ? "text-[#0F6D32]" : "text-red-500"}`}>
              {pnlPct >= 0 ? "+" : ""}{pnlPct.toFixed(2)}%
            </p>
          ) : (
            <p className="text-[10px] text-[#9CA3AF]">No Data</p>
          )}
        </div>
        <div>
          <p className="text-[11px] text-[#697077]">Win Rate</p>
          <p className="text-sm font-semibold text-[#21272A]">{winRate != null ? `${winRate}%` : "—"}</p>
        </div>
      </div>
      <div>
        <p className="text-[11px] text-[#697077]">Trading Capital</p>
        <p className="text-sm font-semibold text-[#21272A]">{fmt(capital, 2)} USDT</p>
        {/* LS capital is split 50/50 across the two legs */}
        {isDualSide && capital > 0 && (
          <p className="text-[10px] text-[#9CA3AF] mt-0.5">
            ({fmt(capital / 2, 2)} USDT (L) + {fmt(capital / 2, 2)} USDT (S))
          </p>
        )}
      </div>

      {/* Footer */}
      <div className="flex items-center justify-between pt-2 border-t border-gray-100">
        <p className="text-[10px] text-[#697077]">Since {fmtDate(bot.created_at)}</p>
        <button
          onClick={() => onView(bot)}
          className="text-xs font-semibold text-[#302571] bg-[#ECEAF8] rounded-lg px-3 py-1.5 hover:bg-[#e1dcef] transition-colors shrink-0"
        >
          Detail
        </button>
      </div>
    </div>
  );
}
